import leaflet from 'leaflet';
import './BoundaryCanvas';

import {helper, http, api} from '../common/common';

class Layer {
    constructor(viewStack, map, getAccessCode, onError) {
        this._viewStack = viewStack;
        this._map = map;
        this._getAccessCode = getAccessCode;
        this._onError = onError;
        this._retryMap = {};

        this._cancelToken = http.CancelToken.source();
    }

    unmount = () => {
        if (this._cancelToken) {
            this._cancelToken.cancel();
        }
    }

    update = async (ejectedStack = []) => {
        try {
            const view = this._viewStack.peek();
            var prevView = null;

            if (ejectedStack.length === 0) {
                prevView = this._viewStack.peek(1);
            } else {
                prevView = ejectedStack[ejectedStack.length - 1];
            }

            const isBase = this._viewStack.isDefaultBase();

            await this._doUpdate(view, prevView, isBase);
        } catch (error) {
            const e = helper.sanitizeError(error, 'Layer updated failed', 'Unable to update layer');
            this._processError(e);
        }
    }

    activate = async (type, index, baseLayerShow, force=false) => {
        try {
            const view = this._viewStack.peek();

            if (index === null) {
                index = this._guessLayerIndex(view, type, view);
            }

            if (baseLayerShow === null) {
                baseLayerShow = view.layer[type].data[index].baseLayerShow;
            }

            await this._doActivate(view, type, index, baseLayerShow, force);
        } catch (error) {
            const e = helper.sanitizeError(error, 'Layer activate failed', 'Unable to activate layer');
            this._processError(e);
        }
    }

    _doUpdate = async (view, prevView, isBase) => {
        if (isBase) {
            await this._loadCompleted(view, prevView);
        } else {
            await this._loadDescription(view, prevView);
        }
    }

    _doActivate = async (view, type, index, baseLayerShow, force=false) => {
        if (!view.layer) {
            return;
        }

        if (!force &&
            view.selectedLayerType === type &&
            view.layer[type].selectedLayerIndex === index &&
            view.layer[type].data[index].baseLayerShow === baseLayerShow) {
            return;
        }

        if (view.selectedLayerType) {
            const oldLayerType = view.selectedLayerType;
            const oldLayerIndex = view.layer[oldLayerType].selectedLayerIndex;

            if (oldLayerType !== type || oldLayerIndex !== index) {
                if (view.layer[oldLayerType].data[oldLayerIndex].obj) {
                    view.layer[oldLayerType].data[oldLayerIndex].obj.remove();
                }
            }

            if (view.layer[oldLayerType].data[oldLayerIndex].base) {
                const baseOverlayArr = view.layer[oldLayerType].data[oldLayerIndex].base;
                const baseOverlayLength = baseOverlayArr.length;

                for (var i = 0; i < baseOverlayLength; ++i) {
                    const oldBaseType = baseOverlayArr[i].key;
                    const oldBaseIndex = baseOverlayArr[i].index;

                    if (view.layer[oldBaseType].data[oldBaseIndex].obj) {
                        view.layer[oldBaseType].data[oldBaseIndex].obj.remove();
                    }
                }
            }
        }

        if (view.layer[type]) {
            if (force ||
                view.selectedLayerType !== type ||
                view.layer[view.selectedLayerType].selectedLayerIndex !== index) {

                if (!view.layer[type].data[index].obj) {
                    view.layer[type].data[index].obj = this._loadLayer(view, type, index);
                }

                if (!this._map.hasLayer(view.layer[type].data[index].obj)) {
                    this._addLayerToMap(view, type, index);
                }
            }

            if (view.layer[type].data[index].base) {
                const baseOverlayArr = view.layer[type].data[index].base;
                const baseOverlayLength = baseOverlayArr.length;

                for (i = 0; i < baseOverlayLength; ++i) {
                    const baseType = baseOverlayArr[i].key;
                    const baseIndex = view.layer[baseType].unsortData[baseOverlayArr[i].index].sortedIndexKey;

                    if (baseIndex < 0) {
                        continue;
                    }

                    if (type === baseType && index === baseIndex) {
                        continue;
                    }

                    if (baseLayerShow) {
                        if (!view.layer[baseType].data[baseIndex].obj) {
                            view.layer[baseType].data[baseIndex].obj = this._loadLayer(view, baseType, baseIndex);
                        }

                        this._addLayerToMap(view, baseType, baseIndex);
                    } else {
                        if (view.layer[baseType].data[baseIndex].obj) {
                            view.layer[baseType].data[baseIndex].obj.remove();
                        }
                    }
                }
            }

            if (view.layer[type].data[index].obj) {
                view.layer[type].data[index].obj.bringToFront();
            }

            view.selectedLayerType = type;
            view.layer[type].selectedLayerIndex = index;
            view.layer[type].data[index].baseLayerShow = baseLayerShow;
        }
    }

    _addLayerToMap = (view, type, index) => {
        if (view !== this._viewStack.peek()) {
            return
        }

        if (view.layer[type].data[index].obj) {
            if (!this._map.hasLayer(view.layer[type].data[index].obj)) {
                view.layer[type].data[index].obj.addTo(this._map);
            }
        }
    }

    _loadCompleted = async (view, prevView) => {
        const type = this._guessLayerType(view, prevView);
        const index = this._guessLayerIndex(view, type, prevView);
        const baseLayerShow = view.layer[type].data[index].baseLayerShow;

        await this._doActivate(view, type, index, baseLayerShow, true);
    }

    _loadDescription = async (view, prevView) => {
        if (view.layer) {
            await this._loadCompleted(view, prevView);
            return;
        }

        await this._requestLayerStack(view, prevView);
    }

    _loadLayer = (view, type, index) => {
        var key = '__base__';
        var tmsMode = false;

        if (view.layer && view.layer[type] && view.layer[type].data[index].tile) {
            key = view.layer[type].data[index].tile;
            tmsMode = true;
        }

        const path = api.map() +
            '/tile/{z}/{x}/{y}?access_code=' + this._getAccessCode() +
            '&org=' + view.organization +
            '&key=' + key;

        if (key === '__base__') {
            const layer = leaflet.tileLayer(path, {
                attribution: '',
                updateInterval: 50,
                keepBuffer: 99,
                maxZoom: 18,
                tms: tmsMode,
                crossOrigin: (process.env.REACT_APP_BUILD_MODE === 'local'),
            });

            this._attachRetry(layer, key);

            return layer;
        } else  {
            const layer = leaflet.TileLayer.boundaryCanvas(path, {
                boundary: view.region.borderData,
                attribution: '',
                updateInterval: 50,
                keepBuffer: 99,
                maxZoom: 18,
                tms: tmsMode,
            });

            this._attachRetry(layer, key);

            return layer;
        }
    }

    _attachRetry = (layer, key) => {
        layer.on({
            tileerror: (e) => {
                if (key in this._retryMap) {
                    this._retryMap[key].hasError = true;
                } else {
                    this._retryMap[key] = {
                        retryLeft: 2,
                        hasError: true,
                    };
                }
            },
            load: (e) => {
                if (key in this._retryMap) {
                    if (this._retryMap[key].hasError === true) {
                        if (this._retryMap[key].retryLeft === 0) {
                            delete this._retryMap[key];

                            this._processError(helper.makeError('Missing Tile',
                                'Unable to retrieve map tiles', 'invalid_client'));
                        } else {
                            this._retryMap[key].retryLeft = this._retryMap[key].retryLeft - 1;
                            this._retryMap[key].hasError = false;

                            e.target.redraw();
                        }
                    } else {
                        delete this._retryMap[key];
                    }
                }
            }
        });
    }

    _sortLayerByDate = (left, right) => {
        if (left.date > right.date) {
            return -1;
        } else if (left.date < right.date) {
            return 1;
        }

        return 0;
    }

    _guessLayerType = (view, prevView) => {
        if (prevView && prevView.selectedLayerType) {
            if (view.layer[prevView.selectedLayerType]) {
                return prevView.selectedLayerType;
            }
        }

        return view.selectedLayerType;
    }

    _guessLayerIndex = (view, layerType, prevView) => {
        if (prevView && prevView.selectedLayerType) {
            const prevLayerType = prevView.selectedLayerType;
            const prevLayerIndex = prevView.layer[prevLayerType].selectedLayerIndex;

            if (prevLayerIndex !== undefined) {
                const targetDateValue = prevView.layer[prevLayerType].data[prevLayerIndex].date;

                if (targetDateValue) {
                    const dataLength = view.layer[layerType].data.length;

                    for (var i = 0; i < dataLength; ++i) {
                        if (view.layer[layerType].data[i].date === targetDateValue) {
                            return i;
                        }
                    }
                }
            }
        }

        if (view.layer && view.layer[layerType]) {
            return view.layer[layerType].selectedLayerIndex;
        }

        return 0;
    }

    _processError = (error) => {
        if (this._onError) {
            this._onError(error);
        }
    }

    _makeDefaultLayer = (view) => {
        view.layer = {
            base: {
                name: 'Base',
                data: [{
                    title: '',
                }],
                selectedLayerIndex: 0,
            }
        };

        view.selectedLayerType = 'base';
    }

    _requestLayerStack = async (view, prevView) => {
        try {
            const response = await http.get(api.map() + '/layer', {
                params: {
                    'access_code': this._getAccessCode(),
                    'organization': view.organization,
                    'region': view.key,
                },
                cancelToken: this._cancelToken.token,
            });

            const layer = Object.entries(response.data.content);

            if (!view.layer) {
                view.layer = {};
            }

            layer.forEach(([index, value]) => {
                view.layer[index] = {
                    ...value,
                };

                view.layer[index].selectedLayerIndex = 0;

                if (view.layer[index].data) {
                    const dataLength = view.layer[index].data.length;

                    for (var i = 0; i < dataLength; ++i) {
                        const l = view.layer[index].data[i];

                        if (!l.date) {
                            l.date = 0;
                        }

                        l.obj = null;
                        l.originalIndexKey = i;

                        if (l.base) {
                            l.baseLayerShow = true;
                        }
                    }

                    view.layer[index].unsortData = view.layer[index].data.slice();
                    view.layer[index].data.sort(this._sortLayerByDate);

                    for (i = 0; i < dataLength; ++i) {
                        view.layer[index].data[i].sortedIndexKey = i;
                    }
                } else {
                    view.layer[index].data = [{tile: ''}];
                    view.layer[index].unsortData = view.layer[index].data.slice();
                }
            });

            if (layer.length === 0) {
                this._makeDefaultLayer(view);
            } else {
                view.selectedLayerType = layer[0][0];
            }


            await this._loadCompleted(view, prevView);

        } catch (error) {
            if (http.isCancel(error)) {
                return;
            }

            this._makeDefaultLayer(view);

            const type = view.selectedLayerType;
            const index = view.layer[type].selectedLayerIndex;

            this._doActivate(view, type, index, false, false);

            const e = helper.sanitizeError(error, 'Layer request failed', 'Unable to complete layer request');
            this._processError(e);
        }
    }

    _logLayerCount = () => {
        var c = 0;
        this._map.eachLayer(() => { ++c; });
        return c;
    }
}

export default Layer;
